/**
 * Impersonation Store
 *
 * Zustand store for support user impersonation ("View As").
 * Persists across app reloads and organisation switches.
 *
 * Stores the Auth0 user ID of the impersonated user, plus display info
 * (name/email) for the active-impersonation banner and their email for pages
 * that show where something will be sent.
 *
 * Consumed by authenticatedFetch (outside React tree via getState())
 * and by the settings UI (inside React via hooks).
 */
import { create } from "zustand";
import { appStorage, AppStorageKeys } from "@src/services/app-storage";

interface ImpersonationState {
  /** Auth0 user ID of the impersonated user (e.g. "auth0|abc123") */
  impersonatedUserId: string | null;
  /** Display label for the active banner (name or email) */
  impersonatedLabel: string | null;
  /** Email of the impersonated user; `null` when the persisted state has none. */
  impersonatedEmail: string | null;
  setImpersonation: (userId: string, label: string, email: string) => void;
  clearImpersonation: () => void;
}

interface PersistedImpersonation {
  impersonatedUserId: string;
  impersonatedLabel: string;
  impersonatedEmail: string | null;
}

const defaultImpersonationState = {
  impersonatedUserId: null,
  impersonatedLabel: null,
  impersonatedEmail: null,
} satisfies Pick<ImpersonationState, "impersonatedUserId" | "impersonatedLabel" | "impersonatedEmail">;

const hasStringProperty = (value: unknown, property: string): boolean =>
  typeof value === "object" && value !== null && property in value && typeof Reflect.get(value, property) === "string";

const getPersistedImpersonation = (): PersistedImpersonation | null => {
  try {
    const rawValue = appStorage.get<unknown>(AppStorageKeys.IMPERSONATION);
    if (!rawValue) {
      return null;
    }

    if (!hasStringProperty(rawValue, "impersonatedUserId") || !hasStringProperty(rawValue, "impersonatedLabel")) {
      return null;
    }

    const impersonatedUserId = Reflect.get(rawValue, "impersonatedUserId");
    const impersonatedLabel = Reflect.get(rawValue, "impersonatedLabel");
    if (typeof impersonatedUserId !== "string" || typeof impersonatedLabel !== "string") {
      return null;
    }
    const impersonatedEmail = Reflect.get(rawValue, "impersonatedEmail");

    return {
      impersonatedUserId,
      impersonatedLabel,
      impersonatedEmail: typeof impersonatedEmail === "string" ? impersonatedEmail : null,
    };
  } catch (error) {
    console.warn("[ImpersonationStore] Failed to read persisted impersonation state", error);
    return null;
  }
};

const persistImpersonation = (value: PersistedImpersonation): void => {
  try {
    appStorage.set(AppStorageKeys.IMPERSONATION, value);
  } catch (error) {
    console.warn("[ImpersonationStore] Failed to persist impersonation state", error);
  }
};

const clearPersistedImpersonation = (): void => {
  try {
    appStorage.remove(AppStorageKeys.IMPERSONATION);
  } catch (error) {
    console.warn("[ImpersonationStore] Failed to clear persisted impersonation state", error);
  }
};

const persistedImpersonation = getPersistedImpersonation();

export const useImpersonationStore = create<ImpersonationState>()((set) => ({
  impersonatedUserId: persistedImpersonation?.impersonatedUserId ?? defaultImpersonationState.impersonatedUserId,
  impersonatedLabel: persistedImpersonation?.impersonatedLabel ?? defaultImpersonationState.impersonatedLabel,
  impersonatedEmail: persistedImpersonation?.impersonatedEmail ?? defaultImpersonationState.impersonatedEmail,

  setImpersonation: (userId: string, label: string, email: string) => {
    const nextState = { impersonatedUserId: userId, impersonatedLabel: label, impersonatedEmail: email };
    persistImpersonation(nextState);
    set(nextState);
  },

  clearImpersonation: () => {
    clearPersistedImpersonation();
    set(defaultImpersonationState);
  },
}));
